/**
 * Hook para acciones comunes sobre tickets
 * 
 * Referencia: sistema-tickets-frontend-COMPLETO.md Sección 6.3
 */

import { useState, useContext } from 'react';
import axios from 'axios';
import { ErpContext } from '../../context/ErpContext';

/**
 * Hook useTicketActions
 * 
 * Hook personalizado para realizar acciones comunes sobre tickets
 * 
 * @returns {Object} - { tomarTicket, reasignarTicket, cambiarEstado, cambiarPrioridad, cambiarCategoria, loading, error }
 * 
 * @example
 * const { tomarTicket, loading, error } = useTicketActions();
 * const exito = await tomarTicket(ticketId);
 */
export const useTicketActions = () => {
  const { serverURL } = useContext(ErpContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  /**
   * Tomar ticket (asignarse a sí mismo)
   * 
   * @param {string} ticketId - ID del ticket
   * @returns {Promise<boolean>} - true si fue exitoso, false si hubo error
   */
  const tomarTicket = async (ticketId) => {
    setLoading(true);
    setError(null);
    
    try {
      await axios.post(
        `${serverURL}/soporteAPI/tickets/${ticketId}/take`,
        {},
        { withCredentials: true }
      );
      return true;
    } catch (err) {
      console.error('Error tomando ticket:', err);
      setError(err.response?.data?.error || 'Error al tomar el ticket');
      return false;
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Reasignar ticket a otro agente
   * 
   * @param {string} ticketId - ID del ticket
   * @param {number} agentId - ID del agente al que se reasigna
   * @returns {Promise<boolean>} - true si fue exitoso, false si hubo error
   */
  const reasignarTicket = async (ticketId, agentId) => {
    setLoading(true);
    setError(null);
    
    try {
      await axios.post(
        `${serverURL}/soporteAPI/tickets/${ticketId}/reassign`,
        { agentId },
        { withCredentials: true }
      );
      return true;
    } catch (err) {
      console.error('Error reasignando ticket:', err);
      setError(err.response?.data?.error || 'Error al reasignar el ticket');
      return false;
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Cambiar estado del ticket
   * 
   * @param {string} ticketId - ID del ticket
   * @param {string} status - Nuevo estado (interno)
   * @returns {Promise<boolean>} - true si fue exitoso, false si hubo error
   */
  const cambiarEstado = async (ticketId, status) => {
    setLoading(true);
    setError(null);
    
    try {
      await axios.patch(
        `${serverURL}/soporteAPI/tickets/${ticketId}/status`,
        { status },
        { withCredentials: true }
      );
      return true;
    } catch (err) {
      console.error('Error cambiando estado:', err);
      setError(err.response?.data?.error || 'Error al cambiar el estado');
      return false;
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Cambiar estado de múltiples tickets (operación masiva)
   * 
   * @param {Array<string>} ticketIds - IDs de los tickets
   * @param {string} status - Nuevo estado (interno)
   * @returns {Promise<Object>} - { exitos: number, fallos: number, errores: Array<string> }
   */
  const cambiarEstadoMasivo = async (ticketIds, status) => {
    setLoading(true);
    setError(null);
    
    const resultados = {
      exitos: 0,
      fallos: 0,
      errores: []
    };
    
    try {
      // Cambiar estado de cada ticket individualmente
      for (const ticketId of ticketIds) {
        try {
          await axios.patch(
            `${serverURL}/soporteAPI/tickets/${ticketId}/status`,
            { status },
            { withCredentials: true }
          );
          resultados.exitos++;
        } catch (err) {
          resultados.fallos++;
          resultados.errores.push(`Ticket ${ticketId}: ${err.response?.data?.error || 'Error al cambiar el estado'}`);
          console.error(`Error cambiando estado del ticket ${ticketId}:`, err);
        }
      }
      
      return resultados;
    } catch (err) {
      console.error('Error en cambio masivo de estado:', err);
      setError(err.response?.data?.error || 'Error en cambio masivo de estado');
      return resultados;
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Cambiar prioridad del ticket
   * 
   * @param {string} ticketId - ID del ticket
   * @param {string} prioridadId - ID de la nueva prioridad
   * @returns {Promise<boolean>} - true si fue exitoso, false si hubo error
   */
  const cambiarPrioridad = async (ticketId, prioridadId) => {
    setLoading(true);
    setError(null);
    
    try {
      await axios.patch(
        `${serverURL}/soporteAPI/tickets/${ticketId}/priority`,
        { prioridadId },
        { withCredentials: true }
      );
      return true;
    } catch (err) {
      console.error('Error cambiando prioridad:', err);
      setError(err.response?.data?.error || 'Error al cambiar la prioridad');
      return false;
    } finally {
      setLoading(false);
    }
  };
  
  /**
   * Cambiar categoría del ticket
   * 
   * @param {string} ticketId - ID del ticket
   * @param {string} categoriaId - ID de la nueva categoría
   * @returns {Promise<boolean>} - true si fue exitoso, false si hubo error
   */
  const cambiarCategoria = async (ticketId, categoriaId) => {
    setLoading(true);
    setError(null);
    
    try {
      await axios.patch(
        `${serverURL}/soporteAPI/tickets/${ticketId}/category`,
        { categoriaId },
        { withCredentials: true }
      );
      return true;
    } catch (err) {
      console.error('Error cambiando categoría:', err);
      setError(err.response?.data?.error || 'Error al cambiar la categoría');
      return false;
    } finally {
      setLoading(false);
    }
  };
  
  return {
    tomarTicket,
    reasignarTicket,
    cambiarEstado,
    cambiarEstadoMasivo,
    cambiarPrioridad,
    cambiarCategoria,
    loading,
    error
  };
};

